/**
 * Mana Pricing — the Pricing and Other Lenders Pricing modules in one page.
 *
 * One scenario, every lender: the shared fields (the rate-sheet catalog) price First Colony,
 * PennyMac and Sun West, and keep UWM's request in step through the field bridge (only the
 * fields the MLO edits are carried into UWM's saved request, so a loaded Pricing scenario sends
 * exactly what it sent before). UWM-only fields — CYP, title, loan officer and the rest of UWM's
 * live layout — sit in their own section. Each lender keeps its own comp and ★ math; the tray,
 * both borrower emails and the schedules cover every lender, and no email names a lender.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  DSCR_FIELD_IDS,
  DSCR_SOURCES,
  FIELD_GROUP_LABELS,
  HOME_EQUITY_SOURCES,
  MANA_PRICING_SOURCES,
  MANA_SOURCE_LABELS,
  MORTGAGE_SOURCES,
  PRICING_FIELDS,
  UWM_COMPANY_NAME_FIELD,
  UWM_DEFAULT_MONTHLY_INCOME,
  amortizationsOf,
  buildLenderRateEntry,
  buildUwmBody,
  buildUwmOptionRows,
  buydownChoicesOf,
  buildRealtorTable,
  defaultEmailTemplate,
  normalizeStateCode,
  realtorGaps,
  realtorRowOf,
  specificArmType,
  type ManaEmailTemplate,
  type RealtorCandidateRow,
  type RealtorColumnKey,
  type RealtorPicks,
  type RealtorRowKey,
  coerceFieldValue,
  fieldDefaults,
  fieldValueList,
  fieldVisible,
  fmtRateAsOf,
  hasDscrLoanType,
  isDscrInputs,
  lenderOptionName,
  loanTypesOf,
  missingUwmFields,
  patchUwmBody,
  seedUwmValues,
  uwmBodyKey,
  uwmRateEntryFor,
  uwmSectionFields,
  uwmValuesFromBody,
  UWM_NO_DSCR_REASON,
  type BuydownChoice,
  type LenderRateRow,
  type ManaPricingSource,
  type PricingFieldGroup,
  type PricingFieldValue,
  type UwmFieldValue,
  type UwmLadderPoint,
  type UwmOptionRow,
  MANA_LANE_SOURCES,
} from '@mana/shared';
import { api } from '../../lib/api.js';
import { Button } from '../../ui/Button.js';
import type { LadderPoint } from '../OtherLendersResults.js';
import { GridEmailCard, OptionsTray, RealtorEmailCard, type EmailScenario } from './EmailCards.js';
import { LendersMenu } from './LendersMenu.js';
import { FieldInput, UwmFieldInput, fieldCls, labelCls } from './FieldInputs.js';
import { ManaResults } from './ManaResults.js';
import { ManaSchedules, SentLogDialog, type SentLogTarget } from './ManaSchedules.js';
import {
  COMPANY_LENDERS_KEY,
  SCHEDULES_KEY,
  applyCanonicalChange,
  applyUwmChange,
  errText,
  fmtMoney0,
  fmtWhen,
  gridParamsFor,
  mergeLaneQuote,
  productTypeOf,
  trayKey,
  uwmCategoryOf,
  uwmErrorLabel,
  type CanonicalValues,
  type CompanyLendersDto,
  type LayoutResponse,
  type LenderSheetHeaderStatus,
  type Msg,
  type ProductType,
  type QuoteApiResponse,
  type ScenarioDto,
  type ScenarioSource,
  type ScheduleDto,
  type ScheduleScenarioDto,
  type SchedulesResponse,
  type SheetQuoteRow,
  type UwmValues,
} from './shared.js';

/** The shared inputs that move the price — always visible in the panel (home-equity fields show for a HELOC / second lien only). */
// Waive Escrows prices on every lender, so it sits with the essentials; UWM's monthly income (blank = $20,000) is under More options.
const ESSENTIAL_IDS = [
  'loanType', 'lienPosition', 'loanPurpose', 'homeEquityProduct', 'occupancy', 'propertyValue', 'seniorLienBalance', 'baseLoanAmount', 'ltv', 'cltv', 'initialDraw',
  'creditScore', 'waiveEscrows', 'propertyZip', 'state', 'term', 'amortizationType', 'desiredLockPeriod', 'desiredPrice', 'compType', 'buydown',
];
/** Lenders that can price the scenario's product type (DSCR: the rate-sheet lenders only — UWM has no DSCR product). */
const sourcesFor = (productType: ProductType, dscr = false): ManaPricingSource[] => (productType === 'heloc' ? HOME_EQUITY_SOURCES : dscr ? DSCR_SOURCES : MORTGAGE_SOURCES);
const GROUPS: PricingFieldGroup[] = ['loan', 'borrower', 'property', 'product'];
/** The Pricing module hides Company Name in production (the org's own brokerage) and always sends its seed. */
const HIDE_COMPANY_NAME = import.meta.env.PROD;
const UWM_VISIBLE_FIELDS = 4;

interface SheetStatus {
  lenders: LenderSheetHeaderStatus[];
}

interface QuoteSnapshot {
  data: QuoteApiResponse;
  at: Date;
  /** What UWM's rows are built from (the form can change after pricing). */
  uwmLoanAmount: number;
  buydowns: Array<BuydownChoice | null>;
  desiredPrice: number;
  lockDays: number;
  /** The ARM Type the lenders were asked for ("5/6"), null for "All". */
  armType: string | null;
}

export function ManaPricingPage() {
  const qc = useQueryClient();
  const [borrowerName, setBorrowerName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [values, setValuesState] = useState<CanonicalValues>(() => fieldDefaults());
  const valuesRef = useRef(values);
  const setValues = (next: CanonicalValues) => {
    valuesRef.current = next;
    setValuesState(next);
  };
  const [lenders, setLenders] = useState<ManaPricingSource[]>([...MANA_PRICING_SOURCES]);
  // The best-rates email template the MLO chose (null = the one the scenario calls for) and the
  // rates pinned for the Realtor Marketing table — both saved with the scenario.
  const [emailTemplate, setEmailTemplate] = useState<ManaEmailTemplate | null>(null);
  const [realtorPicks, setRealtorPicks] = useState<RealtorPicks>({});
  const [cypBps, setCypBps] = useState(0);
  const [tray, setTray] = useState<LenderRateRow[]>([]);
  const [ratesAsOf, setRatesAsOf] = useState<string | null>(null);
  const [quote, setQuote] = useState<QuoteSnapshot | null>(null);
  const [panelOpen, setPanelOpen] = useState(true); // narrow screens only (always open on lg+)
  const [moreOpen, setMoreOpen] = useState(false);
  const [uwmMoreOpen, setUwmMoreOpen] = useState(false);
  const [panelMsg, setPanelMsg] = useState<Msg>(null);
  const [sentLog, setSentLog] = useState<SentLogTarget | null>(null);
  const driverRef = useRef<'loan' | 'ltv'>('loan');
  const uwmDriverRef = useRef<'loan' | 'ltv'>('loan');
  const productType = productTypeOf(values);

  // --- UWM's live layout and form values -------------------------------------
  const layoutQuery = useQuery<LayoutResponse>({
    queryKey: ['mana-layout', productType],
    queryFn: () => api(`/api/mana-pricing/layout?productType=${productType}`),
    retry: false,
    staleTime: 10 * 60_000,
  });
  const uwmLayout = layoutQuery.data?.uwm?.fields ?? null;
  const uwmLayoutRef = useRef(uwmLayout);
  uwmLayoutRef.current = uwmLayout;
  const uwmUnavailable = layoutQuery.data?.uwmError?.message ?? (layoutQuery.isError ? errText(layoutQuery.error, 'UWM Pricing is not reachable right now.') : null);

  // Lenders an admin turned off for the company: hidden from the picks and never priced.
  const companyQuery = useQuery<CompanyLendersDto>({ queryKey: COMPANY_LENDERS_KEY, queryFn: () => api('/api/mana-pricing/company-lenders'), staleTime: 60_000 });
  const lendersOff = useMemo(() => new Set<ManaPricingSource>((companyQuery.data?.lenders ?? []).filter((l) => !l.included).map((l) => l.key)), [companyQuery.data]);

  const [uwmValues, setUwmValuesState] = useState<UwmValues>({});
  const uwmValuesRef = useRef(uwmValues);
  const setUwmValues = (next: UwmValues) => {
    uwmValuesRef.current = next;
    setUwmValuesState(next);
  };
  /** Keys of a loaded UWM request that aren't layout fields (sent along unchanged). */
  const uwmExtrasRef = useRef<Record<string, unknown>>({});
  /** A loaded UWM request waiting for its layout; body null = build UWM's form from the shared fields. */
  const pendingUwmRef = useRef<{ body: Record<string, unknown> | null } | null>({ body: null });
  const seededForRef = useRef<ProductType | null>(null);
  /** The loaded UWM request, sent as-is when UWM's layout can't be fetched. */
  const savedUwmBodyRef = useRef<Record<string, unknown> | null>(null);
  const [syncNonce, setSyncNonce] = useState(0);

  useEffect(() => {
    if (!uwmLayout) return;
    const pending = pendingUwmRef.current;
    if (!pending && seededForRef.current === productType) return;
    pendingUwmRef.current = null;
    seededForRef.current = productType;
    const seed = seedUwmValues(uwmLayout);
    let next: UwmValues = seed;
    if (pending?.body) {
      next = uwmValuesFromBody(uwmLayout, pending.body, seed);
      if (HIDE_COMPANY_NAME && UWM_COMPANY_NAME_FIELD in seed) next[UWM_COMPANY_NAME_FIELD] = seed[UWM_COMPANY_NAME_FIELD]!;
      const layoutKeys = new Set(uwmLayout.map((f) => uwmBodyKey(f.itemId)));
      uwmExtrasRef.current = Object.fromEntries(Object.entries(pending.body).filter(([k]) => !layoutKeys.has(k)));
    } else {
      uwmExtrasRef.current = {};
      const patched = patchUwmBody({ layout: uwmLayout, body: buildUwmBody(uwmLayout, seed), canonicalNow: valuesRef.current, canonicalBaseline: null, productType });
      next = uwmValuesFromBody(uwmLayout, patched.body, seed);
    }
    setUwmValues(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uwmLayout, productType, syncNonce]);

  /** Edit a shared field; carry the change into UWM's request (only the keys it actually changes). */
  const setField = (id: string, v: PricingFieldValue) => {
    const prev = valuesRef.current;
    const next = applyCanonicalChange(prev, id, v, driverRef);
    setValues(next);
    const layout = uwmLayoutRef.current;
    const pt = productTypeOf(next);
    if (layout && seededForRef.current === pt && productTypeOf(prev) === pt) {
      const u = uwmValuesRef.current;
      const patched = patchUwmBody({ layout, body: buildUwmBody(layout, u, uwmExtrasRef.current), canonicalNow: next, canonicalBaseline: prev, productType: pt });
      if (patched.changedKeys.length) setUwmValues(uwmValuesFromBody(layout, patched.body, u));
    }
  };
  const setUwmField = (itemId: string, v: UwmFieldValue) => {
    const layout = uwmLayoutRef.current;
    if (layout) setUwmValues(applyUwmChange(uwmValuesRef.current, itemId, v, layout, uwmDriverRef));
  };

  const uwmBodyNow = (): Record<string, unknown> | null => {
    const layout = uwmLayoutRef.current;
    if (!layout || seededForRef.current !== productTypeOf(valuesRef.current)) return savedUwmBodyRef.current;
    return buildUwmBody(layout, uwmValuesRef.current, uwmExtrasRef.current);
  };
  // The picks sent with a quote or a schedule are the chips as they stand — the server keeps the
  // ones turned OFF, so a lender added later is priced without re-saving. The sources a loan
  // type can't use (UWM on DSCR, Spring EQ on a first lien) are left out by the server, not
  // recorded as turned off.
  /** DSCR is the only loan type (UWM sits out); hasDscr = DSCR is one of them (its inputs show). */
  const isDscr = isDscrInputs(values);
  const hasDscr = hasDscrLoanType(values);
  const uwmCategory = useMemo(() => (uwmLayout ? uwmCategoryOf(productType, uwmLayout, uwmValues) : null), [productType, uwmLayout, uwmValues]);
  const uwmPicked = lenders.includes('uwm') && !lendersOff.has('uwm') && !isDscr;

  // --- saved-scenario typeahead --------------------------------------------------
  const [showSuggest, setShowSuggest] = useState(false);
  const [debounced, setDebounced] = useState('');
  const nameBoxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(borrowerName.trim()), 250);
    return () => window.clearTimeout(t);
  }, [borrowerName]);
  const suggestQuery = useQuery<{ scenarios: Array<{ borrowerName: string; lastQuotedAt: string | null; source: ScenarioSource }> }>({
    queryKey: ['mana-scenarios', productType, debounced],
    queryFn: () => api(`/api/mana-pricing/scenarios?productType=${productType}&q=${encodeURIComponent(debounced)}`),
    enabled: showSuggest,
    retry: false,
  });
  useEffect(() => {
    if (!showSuggest) return;
    const onDoc = (e: MouseEvent) => {
      if (nameBoxRef.current && !nameBoxRef.current.contains(e.target as Node)) setShowSuggest(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [showSuggest]);

  /** Put a loaded scenario in the panel (the form, the picks, the tray, UWM's saved request). */
  const applyScenario = (s: ScenarioDto, overrides: { email?: string } = {}) => {
    const next: CanonicalValues = { ...fieldDefaults() };
    for (const f of PRICING_FIELDS) {
      const sv = s.inputs[f.id];
      if (sv === undefined || sv === null) continue;
      const coerced = coerceFieldValue(f, sv);
      if (Array.isArray(coerced) && coerced.length === 0) continue; // keep the default
      next[f.id] = coerced;
    }
    if (s.productType === 'heloc') next.loanType = 'HELOC';
    setBorrowerName(s.borrowerName);
    setEmail(overrides.email ?? s.email ?? '');
    setPhone(s.phone ?? '');
    setValues(next);
    setLenders(s.lenders.length ? s.lenders : [...MANA_PRICING_SOURCES]);
    setCypBps(s.cypBps ?? 0);
    setEmailTemplate(s.emailTemplate ?? null);
    setRealtorPicks(s.realtorPicks ?? {});
    setTray((s.rates ?? []).filter((r) => r.lender || r.product || r.rate));
    setRatesAsOf(s.ratesUpdatedAt);
    setQuote(null);
    savedUwmBodyRef.current = s.uwmInputs;
    pendingUwmRef.current = { body: s.uwmInputs };
    seededForRef.current = null;
    setSyncNonce((n) => n + 1);
  };

  const loadScenario = async (name: string, source: ScenarioSource) => {
    setShowSuggest(false);
    setPanelMsg(null);
    try {
      const s = await api<ScenarioDto>(`/api/mana-pricing/scenarios/${productType}/${encodeURIComponent(name)}${source === 'mana' ? '' : `?source=${source}`}`);
      applyScenario(s);
      const notes = s.notes?.length ? ` ${s.notes.join(' ')}` : '';
      setPanelMsg({
        tone: 'info',
        text:
          s.source === 'pricing'
            ? `Loaded from the Pricing module — UWM keeps its saved request; review the shared fields for the rate sheets, then Get pricing saves it here.${notes}`
            : s.source === 'other_lenders'
              ? `Loaded from Other Lenders Pricing — review the UWM fields, then Get pricing saves it here.${notes}`
              : s.lastQuotedAt
                ? `Loaded · last priced ${fmtWhen(s.lastQuotedAt)}`
                : 'Loaded saved scenario',
      });
    } catch (e) {
      setPanelMsg({ tone: 'err', text: errText(e, 'Could not load that scenario.') });
    }
  };

  /** A schedule was clicked: show the scenario its emails send (the borrower's saved scenario), ready to edit and Save. */
  const openScheduleScenario = async (s: ScheduleDto) => {
    setShowSuggest(false);
    setPanelMsg(null);
    try {
      const r = await api<ScheduleScenarioDto>(`/api/mana-pricing/schedules/${s.id}/scenario`);
      applyScenario(r.scenario, { email: r.toEmail });
      const what = s.format === 'grid' ? 'best-rates' : 'options';
      setPanelMsg({
        tone: 'info',
        text: r.fromSchedule
          ? `Showing what ${s.borrowerName}'s recurring ${what} email sends (next ${fmtWhen(s.nextRunAt)}). ${r.scenario.notes.join(' ')}`
          : `Showing the saved scenario ${s.borrowerName}'s recurring ${what} email sends (next ${fmtWhen(s.nextRunAt)} · saved ${fmtWhen(r.scenario.updatedAt ?? null)}). Edit and Save to change what it sends.`,
      });
      setPanelOpen(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setPanelMsg({ tone: 'err', text: errText(e, "Could not load that schedule's scenario.") });
    }
  };

  const deleteScenario = useMutation({
    mutationFn: (name: string) => api(`/api/mana-pricing/scenarios/${productType}/${encodeURIComponent(name)}`, { method: 'DELETE' }),
    onSuccess: (_r, name) => {
      if (name === borrowerName.trim()) setBorrowerName('');
      void suggestQuery.refetch();
    },
  });

  // --- persistence -------------------------------------------------------------
  const selection = useMemo(() => tray.map((t) => t.productId).filter((id): id is string => !!id), [tray]);
  const saveMut = useMutation({
    mutationFn: () =>
      api<{ scenario: ScenarioDto }>('/api/mana-pricing/scenarios', {
        method: 'POST',
        body: JSON.stringify({
          borrowerName: borrowerName.trim(),
          email,
          phone,
          productType: productTypeOf(valuesRef.current),
          inputs: valuesRef.current,
          uwmInputs: uwmBodyNow(),
          // The MLO's picks (not just what's reachable right now), so a UWM outage doesn't unpick UWM.
          lenders,
          cypBps,
          uwmCategory,
          rates: tray,
          selection,
          emailTemplate,
          realtorPicks,
        }),
      }),
    onSuccess: (r) => {
      setRatesAsOf(r.scenario.ratesUpdatedAt);
      // Recurring emails send the saved scenario, so a save changes what they send from the next send on.
      const linked = (schedulesQuery.data?.schedules ?? []).filter((s) => s.borrowerName === r.scenario.borrowerName && s.productType === r.scenario.productType && s.active);
      setPanelMsg({
        tone: 'ok',
        text: linked.length
          ? `Saved “${r.scenario.borrowerName}” · ${linked.length === 1 ? `the recurring ${linked[0]!.format === 'grid' ? 'best-rates' : 'options'} email sends it from the next send (${fmtWhen(linked[0]!.nextRunAt)})` : `${linked.length} recurring emails send it from their next send`}`
          : `Saved “${r.scenario.borrowerName}”`,
      });
      void qc.invalidateQueries({ queryKey: ['mana-scenarios'] });
      void qc.invalidateQueries({ queryKey: SCHEDULES_KEY });
    },
    onError: (e) => setPanelMsg({ tone: 'err', text: errText(e, 'Save failed') }),
  });
  // The options tray auto-saves (debounced) once a borrower is named.
  const trayDirty = useRef(false);
  const saveMutRef = useRef(saveMut);
  saveMutRef.current = saveMut;
  useEffect(() => {
    if (!trayDirty.current || !borrowerName.trim()) return;
    const t = window.setTimeout(() => {
      trayDirty.current = false;
      saveMutRef.current.mutate();
    }, 800);
    return () => window.clearTimeout(t);
  }, [tray, borrowerName]);
  const updateTray = (fn: (prev: LenderRateRow[]) => LenderRateRow[]) => {
    trayDirty.current = true;
    setTray(fn);
    setRatesAsOf(new Date().toISOString());
  };
  const pushTray = (entry: LenderRateRow) => updateTray((prev) => (prev.some((r) => trayKey(r) === trayKey(entry)) ? prev : [...prev, entry]));

  // --- pricing ---------------------------------------------------------------------
  // A quote is asked in several requests sent together: "fast" (the sheet-only sources, back in a
  // second) shows as soon as it lands and names the lanes still pricing; one lane per picked UWM /
  // live engine (started at the same moment) is folded in as it lands — unless a newer quote has
  // started. An engine that hangs delays only its own lane, which stays marked "pricing…".
  const quoteSeq = useRef(0);
  const quoteRef = useRef(quote);
  useEffect(() => {
    quoteRef.current = quote;
  }, [quote]);
  const quoteMsg = (data: QuoteApiResponse) => {
    const count = (data.sheets.status === 'ok' ? data.sheets.quote.rows.length : 0) + (data.uwm.status === 'ok' ? (data.uwm.result.validQuoteItems?.length ?? 0) : 0);
    const failed = [data.uwm.status === 'error' ? 'UWM' : null, data.sheets.status === 'error' ? 'the rate sheets' : null].filter(Boolean);
    const waiting = (data.pending ?? []).map((l) => MANA_SOURCE_LABELS[l as ManaPricingSource] ?? l);
    setPanelMsg({
      tone: failed.length ? 'err' : 'ok',
      text: `Saved “${data.savedAs}” · ${count} product${count === 1 ? '' : 's'}${waiting.length ? ` · ${waiting.join(', ')} pricing…` : ''}${failed.length ? ` · ${failed.join(' and ')} not priced — see Results` : ''}`,
    });
  };
  const quoteMut = useMutation({
    mutationFn: async () => {
      const v = valuesRef.current;
      const uwmInputs = uwmBodyNow();
      const snapshot = {
        uwmLoanAmount: Number(uwmInputs?.loanAmount) || 0,
        buydowns: buydownChoicesOf(v),
        desiredPrice: Number(String(v.desiredPrice ?? '')) || 100,
        lockDays: Number(String(v.desiredLockPeriod ?? '').match(/\d+/)?.[0]) || 30,
        armType: specificArmType(v.armType),
      };
      const body = { borrowerName: borrowerName.trim(), email, phone, productType: productTypeOf(v), inputs: v, uwmInputs, lenders, cypBps, uwmCategory, selection, emailTemplate, realtorPicks };
      const seq = ++quoteSeq.current;
      // The lanes (UWM and each live engine) start now, not after the fast answer — the server
      // answers a lane at once when that source is off or sheet-priced, and the fast answer says
      // which lanes are really pending.
      const lanes = lenders
        .filter((l) => MANA_LANE_SOURCES.includes(l))
        .map((key) => ({
          key,
          result: api<QuoteApiResponse>('/api/mana-pricing/quote', { method: 'POST', body: JSON.stringify({ ...body, phase: 'slow', only: [key] }) }).then(
            (r) => ({ ok: true as const, r }),
            (e: unknown) => ({ ok: false as const, e }),
          ),
        }));
      const data = await api<QuoteApiResponse>('/api/mana-pricing/quote', { method: 'POST', body: JSON.stringify({ ...body, phase: 'fast', lanes: true }) });
      return { data, snapshot, seq, lanes };
    },
    onSuccess: ({ data, snapshot, seq, lanes }) => {
      const snap = { data, at: new Date(), ...snapshot };
      quoteRef.current = snap; // before React commits it, in case a lane answers at once
      setQuote(snap);
      setPanelOpen(false);
      quoteMsg(data);
      void qc.invalidateQueries({ queryKey: ['mana-scenarios'] });
      for (const lane of lanes) {
        const name = MANA_SOURCE_LABELS[lane.key] ?? lane.key;
        void lane.result.then((res) => {
          const cur = quoteRef.current;
          if (seq !== quoteSeq.current || !cur) return; // a newer quote replaced this one
          const failed = (message: string): QuoteApiResponse =>
            lane.key === 'uwm'
              ? { ...cur.data, uwm: { status: 'error', code: 'lane_failed', message, fieldErrors: null, retryAfterSeconds: null }, sheets: { status: 'skipped', reason: 'No rate-sheet lender is picked.' }, pending: [] }
              : { ...cur.data, uwm: { status: 'skipped', reason: 'UWM is not picked.' }, sheets: { status: 'error', message }, pending: [] };
          const merged = mergeLaneQuote(cur.data, res.ok ? res.r : failed(errText(res.e, 'could not be priced')), [lane.key], [name]);
          const next = { ...cur, data: merged };
          quoteRef.current = next; // lanes landing in the same tick stack up instead of overwriting each other
          setQuote(next);
          quoteMsg(merged);
          if (!(merged.pending ?? []).length) void qc.invalidateQueries({ queryKey: ['mana-scenarios'] });
        });
      }
    },
    onError: (e) => setPanelMsg({ tone: 'err', text: errText(e, 'Pricing failed') }),
  });
  const refreshMut = useMutation({
    mutationFn: () => api<{ ok: boolean; errors: string[] }>('/api/mana-pricing/sheets/refresh', { method: 'POST', body: '{}' }),
    onSuccess: (r) => {
      if (r.errors.length) setPanelMsg({ tone: 'err', text: r.errors.join(' | ') });
      void qc.invalidateQueries({ queryKey: ['mana-sheets'] });
      quoteMut.mutate();
    },
    onError: (e) => setPanelMsg({ tone: 'err', text: errText(e, 'Refresh failed') }),
  });

  const uwmRows = useMemo<UwmOptionRow[]>(() => {
    if (!quote || quote.data.uwm.status !== 'ok') return [];
    return buildUwmOptionRows({
      result: quote.data.uwm.result,
      buydowns: quote.buydowns,
      loanAmount: quote.uwmLoanAmount,
      cypBps,
      criteria: quote.data.uwm.criteria,
      criteriaByFamily: quote.data.uwm.criteriaByFamily ?? null,
      lpCompPct: quote.data.uwm.lpCompPct,
      desiredPrice: quote.desiredPrice,
      lockDays: quote.lockDays,
    });
  }, [quote, cypBps]);

  // Realtor Marketing: every priced 30-yr conventional option (UWM and the lenders) as a candidate
  // for the 5/6 ARM · fixed · lender-paid 2/1 rows, the 3 × 3 table it makes with the pinned rates,
  // and the pin buttons the results show.
  const realtorRows = useMemo<RealtorCandidateRow[]>(() => {
    const out: RealtorCandidateRow[] = [];
    const quotedAt = quote?.data.uwm.status === 'ok' ? quote.data.uwm.quotedAt : null;
    for (const r of uwmRows) {
      out.push({ optionId: r.optionId, source: 'uwm', lender: r.investor, product: r.product, termYears: r.termYears, amortization: r.amortization ?? 'fixed', armType: null, buydown: r.buydown ? { base: r.buydown.base, paidBy: r.buydown.paidBy } : null, loanType: r.loanFamily && r.loanFamily !== 'conventional' ? r.loanFamily : 'Conventional', asOf: quotedAt, ladder: r.ladder.map((p) => ({ rate: p.rate, price: p.price, apr: p.apr, pi: p.pi, credit: p.discountDollars, star: p.meetsCriteria })) });
    }
    const sheetRows = quote?.data.sheets.status === 'ok' ? (quote.data.sheets.quote.rows as SheetQuoteRow[]) : [];
    // The lenders price the ARM type the scenario asked for (their product names don't always say which).
    const armType = quote?.armType ?? null;
    for (const r of sheetRows) {
      out.push({ optionId: r.optionId, source: r.lenderKey as ManaPricingSource, lender: r.investor, product: r.product, termYears: r.termYears, amortization: r.amortization ?? 'fixed', armType: r.amortization === 'arm' ? armType : null, buydown: r.buydown ? { base: r.buydown.base, paidBy: r.buydown.paidBy } : null, loanType: r.loanType ?? 'Conventional', asOf: r.sheetAt ?? null, ladder: r.ladder.map((p) => ({ rate: p.rate, price: p.price, apr: p.apr, pi: p.pi, credit: p.discountDollars, star: p.meetsCriteria })) });
    }
    return out;
  }, [quote, uwmRows]);
  const realtorTable = useMemo(() => buildRealtorTable(realtorRows, realtorPicks, quote?.desiredPrice ?? 100), [realtorRows, realtorPicks, quote?.desiredPrice]);
  const realtorRowKeys = useMemo(() => new Map(realtorRows.map((r) => [r.optionId, realtorRowOf(r)])), [realtorRows]);
  const realtorControls = useMemo(
    () => ({
      rowOf: (optionId: string) => realtorRowKeys.get(optionId) ?? null,
      parPriceOf: (row: RealtorRowKey) => realtorTable.cells.find((c) => c.row === row && c.column === 'par')?.winner?.price ?? null,
      picks: realtorPicks,
      onPick: (row: RealtorRowKey, column: RealtorColumnKey, optionId: string, rate: number) =>
        setRealtorPicks((prev) => {
          const cur = prev[row]?.[column];
          const next = { ...prev, [row]: { ...(prev[row] ?? {}) } } as RealtorPicks;
          if (cur && cur.optionId === optionId && Math.abs(cur.rate - rate) < 1e-6) delete next[row]![column];
          else next[row]![column] = { optionId, rate };
          if (!Object.keys(next[row]!).length) delete next[row];
          return next;
        }),
    }),
    [realtorRowKeys, realtorPicks, realtorTable],
  );
  /** Point the scenario at the five programs the realtor table shows (then Get pricing): FHA + ARM covers the FHA 5/1 ARM row. */
  const setupRealtorScenario = () => setValues({ ...values, loanType: ['Conventional', 'FHA'], term: ['30 Years'], amortizationType: ['Fixed', 'ARM'], armType: '5/6', buydown: ['None', '2/1 Lender-paid'] });

  const addSheetRate = (row: SheetQuoteRow, p: LadderPoint) =>
    pushTray(
      buildLenderRateEntry({
        productId: row.optionId,
        investor: row.investor,
        product: lenderOptionName(row.product, row.termYears, row.buydown?.label),
        rate: p.rate,
        price: p.price,
        apr: p.apr,
        pi: p.pi,
        discountDollars: p.discountDollars,
        lockPeriod: row.lockPeriod,
        lastUpdated: row.lastUpdated,
        sheetVersion: row.sheetVersion,
        comp: row.comp,
        extraNote: row.buydown?.paidBy === 'borrower' && p.buydownCost != null ? `${row.buydown.label} buydown escrow ${fmtMoney0(p.buydownCost)} (not in the price)` : null,
        asOf: fmtRateAsOf(row.sheetAt),
      }),
    );
  const addUwmRate = (row: UwmOptionRow, p: UwmLadderPoint) => pushTray(uwmRateEntryFor(row, p, quote?.data.uwm.status === 'ok' ? quote.data.uwm.quotedAt : null));
  const inTray = (optionId: string, rate: number) => tray.some((r) => r.productId === optionId && r.rate === `${rate.toFixed(3)}%`);

  // --- status ------------------------------------------------------------------------
  const sheetsQuery = useQuery<SheetStatus>({ queryKey: ['mana-sheets'], queryFn: () => api('/api/mana-pricing/sheets'), staleTime: 60_000, retry: false });
  const schedulesQuery = useQuery({ queryKey: SCHEDULES_KEY, queryFn: () => api<SchedulesResponse>('/api/mana-pricing/schedules') });

  // --- validation --------------------------------------------------------------------
  const uwmSection = useMemo(
    () => (uwmLayout ? uwmSectionFields(uwmLayout, uwmValues, { hideCompanyName: HIDE_COMPANY_NAME, productType }) : []),
    [uwmLayout, uwmValues, productType],
  );
  const missing = useMemo(() => {
    const out: string[] = [];
    for (const f of PRICING_FIELDS) {
      if (!f.required || !fieldVisible(f, values)) continue;
      const v = values[f.id];
      if (Array.isArray(v) ? v.length === 0 : !String(v ?? '').trim()) out.push(f.label);
    }
    if (uwmLayout && uwmPicked) out.push(...missingUwmFields(uwmSection, uwmValues).map((f) => `UWM ${f.label}`));
    return out;
  }, [values, uwmLayout, uwmPicked, uwmSection, uwmValues]);
  const availableSources = sourcesFor(productType, isDscr).filter((l) => !lendersOff.has(l));
  const pricedLenders = lenders.filter((l) => availableSources.includes(l) && (l !== 'uwm' || !!uwmLayout));
  const priceBlock = !borrowerName.trim()
    ? 'Enter the borrower name — pricing saves the scenario under it'
    : pricedLenders.length === 0
      ? availableSources.length === 0
        ? 'Every lender for this product type is turned off for the company'
        : productType === 'heloc'
          ? `Pick a home-equity lender (${availableSources.map((l) => MANA_SOURCE_LABELS[l]).join(' or ')})`
          : 'Pick at least one lender'
      : missing.length
        ? `Missing: ${missing.join(', ')}`
        : null;
  const uwmFieldErrors = quote?.data.uwm.status === 'error' ? quote.data.uwm.fieldErrors : null;

  const emailScenario = (): EmailScenario => {
    const v = valuesRef.current;
    return {
      borrowerName: borrowerName.trim(),
      productType: productTypeOf(v),
      inputs: v,
      uwmInputs: uwmBodyNow(),
      lenders,
      cypBps,
      uwmCategory,
      params: gridParamsFor(uwmLayoutRef.current, uwmValuesRef.current, v),
      template: emailTemplate,
      realtorPicks,
    };
  };
  const hasGridSchedule = (schedulesQuery.data?.schedules ?? []).some((s) => s.format === 'grid' && s.borrowerName === borrowerName.trim());

  const summary = useMemo(() => {
    const parts: string[] = [];
    const la = Number(String(values.baseLoanAmount ?? '').replace(/[^0-9.]/g, '')) || 0;
    const pv = Number(String(values.propertyValue ?? '').replace(/[^0-9.]/g, '')) || 0;
    if (la) parts.push(fmtMoney0(la) + (pv ? ` / ${fmtMoney0(pv)}` : ''));
    if (la && pv) parts.push(`${((la / pv) * 100).toFixed(1)}% LTV`);
    if (String(values.creditScore ?? '').trim()) parts.push(`FICO ${String(values.creditScore)}`);
    parts.push(`${loanTypesOf(values).join(' / ')}${/second/i.test(String(values.lienPosition ?? '')) ? ' 2nd lien' : ''} ${String(values.loanPurpose ?? '')}`.trim());
    parts.push(`${fieldValueList(values.term).map((t) => t.replace(/\s*Years?$/i, '')).join('/')} yr ${productType === 'heloc' ? '' : amortizationsOf(values).join(' + ').toLowerCase()}`.trim());
    parts.push(pricedLenders.map((l) => MANA_SOURCE_LABELS[l as ManaPricingSource]).join(', '));
    return parts.filter(Boolean).join(' · ');
  }, [values, pricedLenders, productType]);

  const withHint = (f: (typeof PRICING_FIELDS)[number]) =>
    f.id === 'monthlyQualifyingIncome'
      ? { ...f, placeholder: String(UWM_DEFAULT_MONTHLY_INCOME), hint: `UWM only — blank = ${fmtMoney0(UWM_DEFAULT_MONTHLY_INCOME)}/month. The rate sheets don't price on income.` }
      : f.id === 'desiredPrice'
        ? { ...f, hint: 'Results show each option at its best rate (★). Only an option with no ★ rate is shown at the rate priced closest to this price.' }
        : f;
  const essentials = ESSENTIAL_IDS.map((id) => PRICING_FIELDS.find((f) => f.id === id)!)
    .filter((f) => fieldVisible(f, values))
    .map(withHint);
  // DSCR loans get their own always-visible section (ratio, rent / PITIA, prepayment, IO, …).
  const dscrFields = hasDscr ? DSCR_FIELD_IDS.map((id) => PRICING_FIELDS.find((f) => f.id === id)!).filter((f) => f && fieldVisible(f, values)).map(withHint) : [];
  const moreFields = PRICING_FIELDS.filter((f) => !ESSENTIAL_IDS.includes(f.id) && f.group !== 'dscr' && !(hasDscr && DSCR_FIELD_IDS.includes(f.id)) && fieldVisible(f, values)).map(withHint);
  const uwmShown = uwmMoreOpen || productType === 'heloc' ? uwmSection : uwmSection.slice(0, UWM_VISIBLE_FIELDS);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-gold-700">Mana Pricing</p>
          <h1 className="mt-1 font-display text-3xl font-bold text-brand-navy-800">Price across lenders</h1>
        </div>
        <LendersMenu company={companyQuery.data} sheets={sheetsQuery.data?.lenders ?? []} uwm={{ loading: layoutQuery.isLoading, error: uwmUnavailable }} />
      </header>

      <div className="lg:grid lg:grid-cols-[340px_minmax(0,1fr)] lg:items-start lg:gap-5">
        {/* ---------------- Scenario: summary bar (narrow) + panel ---------------- */}
        <div className="space-y-3 lg:sticky lg:top-0 lg:max-h-[calc(100vh-5rem)] lg:overflow-y-auto lg:pb-1">
          <div className="surface-card flex items-center justify-between gap-3 px-4 py-3 lg:hidden">
            <div className="min-w-0 text-xs">
              <p className="truncate font-semibold text-brand-navy-800">{borrowerName.trim() || 'New scenario'}</p>
              <p className="truncate text-slate-500">{summary}</p>
            </div>
            <Button size="sm" variant="secondary" onClick={() => setPanelOpen((v) => !v)}>
              {panelOpen ? 'Hide' : 'Edit'}
            </Button>
          </div>

          <aside className={clsx('surface-card lg:block', panelOpen ? 'block' : 'hidden')}>
            <div className="border-b border-slate-100 px-4 pb-3 pt-4">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Scenario</p>
              <div ref={nameBoxRef} className="relative">
                <label className={labelCls}>Borrower *</label>
                <input
                  value={borrowerName}
                  placeholder="Start typing to load a saved scenario…"
                  onChange={(e) => {
                    setBorrowerName(e.target.value);
                    setShowSuggest(true);
                  }}
                  onFocus={() => setShowSuggest(true)}
                  className={fieldCls}
                />
                {showSuggest && (suggestQuery.data?.scenarios.length ?? 0) > 0 && (
                  <ul className="absolute z-30 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 text-sm shadow-floating">
                    {suggestQuery.data!.scenarios.map((s) => (
                      <li key={`${s.source}:${s.borrowerName}`} className="flex items-center hover:bg-brand-navy-50">
                        <button type="button" onClick={() => void loadScenario(s.borrowerName, s.source)} className="flex-1 px-3 py-1.5 text-left text-slate-800">
                          {s.borrowerName}
                          {s.source === 'mana' ? (
                            s.lastQuotedAt && <span className="ml-2 text-[10px] text-slate-400">{fmtWhen(s.lastQuotedAt)}</span>
                          ) : (
                            <span className="ml-2 rounded bg-brand-gold-100 px-1.5 py-px text-[10px] font-medium text-brand-gold-700" title="Saved in an old pricing module — loads translated">
                              {s.source === 'pricing' ? 'Pricing' : 'Other Lenders'}
                            </span>
                          )}
                        </button>
                        {s.source === 'mana' && (
                          <button
                            type="button"
                            title="Delete this saved scenario"
                            disabled={deleteScenario.isPending}
                            onClick={() => {
                              if (window.confirm(`Delete the saved scenario for “${s.borrowerName}”? Any recurring email for this borrower is kept.`)) deleteScenario.mutate(s.borrowerName);
                            }}
                            className="px-2 py-1.5 text-slate-400 hover:text-rose-600 disabled:opacity-50"
                          >
                            ✕
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="mt-2.5 grid grid-cols-2 gap-x-3">
                <div>
                  <label className={labelCls}>Email</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={fieldCls} />
                </div>
                <div>
                  <label className={labelCls}>Phone</label>
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} className={fieldCls} />
                </div>
              </div>
            </div>

            {/* Lenders to price */}
            <div className="border-b border-slate-100 px-4 py-3">
              <p className={labelCls}>Lenders to price</p>
              <div className="flex flex-wrap gap-1.5">
                {availableSources.map((src) => {
                  const on = lenders.includes(src);
                  const disabled = src === 'uwm' && !!uwmUnavailable;
                  return (
                    <button
                      key={src}
                      type="button"
                      aria-pressed={on}
                      disabled={disabled}
                      title={src === 'uwm' && uwmUnavailable ? `UWM isn't available: ${uwmUnavailable}` : undefined}
                      onClick={() => setLenders((prev) => (prev.includes(src) ? prev.filter((l) => l !== src) : MANA_PRICING_SOURCES.filter((l) => l === src || prev.includes(l))))}
                      className={clsx(
                        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors disabled:cursor-not-allowed',
                        on && !(src === 'uwm' && uwmUnavailable) ? 'border-brand-navy-700 bg-brand-navy-700 text-white' : 'border-slate-300 bg-white text-slate-600 hover:border-brand-navy-300',
                        disabled && !on && 'opacity-50',
                      )}
                    >
                      {MANA_SOURCE_LABELS[src]}
                      <span className="text-[10px] font-medium opacity-75">{src === 'uwm' || sheetsQuery.data?.lenders.find((l) => l.key === src)?.liveEngine ? 'live' : 'sheet'}</span>
                    </button>
                  );
                })}
              </div>
              {productType === 'heloc' && <p className="mt-1.5 text-[11px] text-slate-500">Home equity prices on UWM (live HELOC / HELOAN) and Spring EQ's sheet (home equity loan, variable HELOC, fixed-rate HELOC).</p>}
              {isDscr ? (
                <p className="mt-1.5 text-[11px] text-slate-500">{UWM_NO_DSCR_REASON}</p>
              ) : (
                hasDscr && <p className="mt-1.5 text-[11px] text-slate-500">DSCR prices on the rate-sheet lenders only — UWM prices the other loan types.</p>
              )}
              {lendersOff.size > 0 && (
                <p className="mt-1.5 text-[11px] text-slate-400">
                  Off for the company: {MANA_PRICING_SOURCES.filter((s) => lendersOff.has(s)).map((s) => MANA_SOURCE_LABELS[s]).join(', ')}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 px-4 py-3">
              {essentials.map((f) => (
                <FieldInput key={f.id} field={f} value={values[f.id]} values={values} onChange={setField} />
              ))}
            </div>
            {dscrFields.length > 0 && (
              <div className="mx-3 mb-3 rounded-xl border border-brand-navy-100 bg-brand-navy-50/50 px-3 py-3">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-brand-navy-700">DSCR</p>
                  <span className="text-[10px] text-slate-500">investor cash-flow pricing</span>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
                  {dscrFields.map((f) => (
                    <FieldInput key={f.id} field={f} value={values[f.id]} values={values} onChange={setField} />
                  ))}
                </div>
              </div>
            )}
            <div className="border-t border-slate-100">
              <button type="button" onClick={() => setMoreOpen((v) => !v)} className="flex w-full items-center justify-between px-4 py-2.5 text-xs font-medium text-brand-navy-700 hover:bg-slate-50">
                <span>
                  {moreOpen ? 'Hide' : 'More'} options <span className="font-normal text-slate-400">({moreFields.length} more fields)</span>
                </span>
                <span className="text-slate-400">{moreOpen ? '▴' : '▾'}</span>
              </button>
              {moreOpen && (
                <div className="space-y-4 px-4 pb-4">
                  {GROUPS.map((g) => {
                    const fields = moreFields.filter((f) => f.group === g);
                    if (fields.length === 0) return null;
                    return (
                      <div key={g}>
                        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">{FIELD_GROUP_LABELS[g]}</p>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
                          {fields.map((f) => (
                            <FieldInput key={f.id} field={f} value={values[f.id]} values={values} onChange={setField} />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* UWM only */}
            {uwmPicked && (
              <div className="mx-3 mb-3 rounded-xl border border-brand-gold-200 bg-brand-gold-50/60 px-3 py-3">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-brand-gold-700">UWM only</p>
                  <span className="text-[10px] text-slate-500">from UWM's live form</span>
                </div>
                {uwmUnavailable ? (
                  <p className="text-[11px] text-rose-600">UWM isn't available: {uwmUnavailable}</p>
                ) : !uwmLayout ? (
                  <p className="text-[11px] text-slate-500">Loading UWM's fields…</p>
                ) : (
                  <div className="space-y-2.5">
                    <div>
                      <label className={labelCls}>CYP incentive (bps)</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min={-1000}
                          max={1000}
                          value={cypBps}
                          onChange={(e) => setCypBps(Math.max(-1000, Math.min(1000, Math.round(Number(e.target.value) || 0))))}
                          className={clsx(fieldCls, 'w-24')}
                        />
                        <span className="text-[11px] leading-snug text-slate-500">Adjusts UWM's final prices and ★ only. Not sent to UWM.</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
                      {uwmShown.map((f) => (
                        <UwmFieldInput key={f.itemId} field={f} value={uwmValues[f.itemId]} error={uwmFieldErrors?.[uwmBodyKey(f.itemId)]?.join(' ')} onChange={setUwmField} />
                      ))}
                    </div>
                    {uwmSection.length > UWM_VISIBLE_FIELDS && (
                      <button type="button" onClick={() => setUwmMoreOpen((v) => !v)} className="text-xs font-medium text-brand-navy-700 hover:underline">
                        {uwmMoreOpen ? 'Fewer UWM fields' : `More UWM fields (${uwmSection.length - UWM_VISIBLE_FIELDS})`}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="sticky bottom-0 space-y-2 border-t border-slate-100 bg-white px-4 py-3">
              <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-2">
                <Button
                  variant="secondary"
                  disabled={!borrowerName.trim() || saveMut.isPending || quoteMut.isPending}
                  title={!borrowerName.trim() ? 'Enter the borrower name — the scenario is saved under it' : 'Save the scenario without pricing it. Recurring emails send the saved scenario.'}
                  onClick={() => saveMut.mutate()}
                >
                  {saveMut.isPending ? 'Saving…' : 'Save'}
                </Button>
                <Button disabled={!!priceBlock || quoteMut.isPending || saveMut.isPending} title={priceBlock ?? 'Save the scenario and price it on every picked lender'} onClick={() => quoteMut.mutate()}>
                  {quoteMut.isPending ? 'Pricing…' : 'Get pricing'}
                </Button>
              </div>
              {priceBlock && borrowerName.trim() && <p className="truncate text-[11px] text-slate-400">{priceBlock}</p>}
              {panelMsg ? (
                <p className={clsx('text-[11px]', panelMsg.tone === 'ok' ? 'text-emerald-600' : panelMsg.tone === 'err' ? 'text-rose-600' : 'text-slate-500')}>{panelMsg.text}</p>
              ) : (
                <p className="text-[11px] text-slate-400">Save keeps the scenario under the borrower's name; recurring emails always send the saved scenario. Get pricing saves it too.</p>
              )}
            </div>
          </aside>
        </div>

        {/* ---------------- Results, emails, schedules ---------------- */}
        <div className="mt-4 space-y-4 lg:mt-0">
          {quote ? (
            <ManaResults
              quote={quote.data}
              pricedAt={quote.at}
              uwmRows={uwmRows}
              uwmLoanAmount={quote.uwmLoanAmount}
              cypBps={cypBps}
              onAddSheet={addSheetRate}
              onAddUwm={addUwmRate}
              inTray={inTray}
              onRefreshSheets={() => refreshMut.mutate()}
              refreshing={refreshMut.isPending || quoteMut.isPending}
              fieldLabel={(k) => uwmErrorLabel(k, uwmLayout, productType)}
              realtor={realtorControls}
            />
          ) : (
            <div className="surface-card px-6 py-8 text-center">
              <p className="text-sm font-medium text-brand-navy-800">Fill in the scenario and click Get pricing</p>
              <p className="mx-auto mt-1 max-w-md text-xs text-slate-500">Every picked lender is priced at once. Add options for the borrower, or email the best rates now or on a schedule.</p>
            </div>
          )}

          {(quote || hasGridSchedule) && (
            <GridEmailCard
              borrowerName={borrowerName.trim()}
              email={email}
              onEmailChange={setEmail}
              scenario={emailScenario}
              values={values}
              schedules={schedulesQuery.data}
              disabledReason={priceBlock}
              onShowSent={() => setSentLog({ title: `Rates sent to ${borrowerName.trim() || 'this borrower'}`, borrowerName: borrowerName.trim() })}
              template={emailTemplate}
              defaultTemplate={defaultEmailTemplate(values, productType)}
              onTemplateChange={setEmailTemplate}
            />
          )}

          {(quote || (schedulesQuery.data?.schedules ?? []).some((s) => s.format === 'realtor' && s.borrowerName === borrowerName.trim())) && productType === 'mortgage' && (
            <RealtorEmailCard
              borrowerName={borrowerName.trim()}
              scenario={emailScenario}
              cells={realtorTable.cells}
              gaps={realtorGaps(realtorRows).map((g) => g.label)}
              hasQuote={!!quote}
              ratesAsOf={quote ? quote.at.toISOString() : null}
              scenarioState={normalizeStateCode(values.state) ?? null}
              onSetupScenario={setupRealtorScenario}
              schedules={schedulesQuery.data}
              disabledReason={priceBlock}
              onShowSent={() => setSentLog({ title: `Realtor rate sheets sent for ${borrowerName.trim() || 'this scenario'}`, borrowerName: borrowerName.trim() })}
            />
          )}

          <OptionsTray
            borrowerName={borrowerName.trim()}
            email={email}
            onEmailChange={setEmail}
            scenario={emailScenario}
            tray={tray}
            ratesAsOf={ratesAsOf}
            onRemove={(k) => updateTray((prev) => prev.filter((r) => trayKey(r) !== k))}
            onAddManual={(row) => updateTray((prev) => [...prev, row])}
            hasQuote={!!quote}
            schedules={schedulesQuery.data}
          />

          <ManaSchedules data={schedulesQuery.data} onShowSent={setSentLog} onOpenScenario={(s) => void openScheduleScenario(s)} />
        </div>
      </div>
      {sentLog && <SentLogDialog target={sentLog} onClose={() => setSentLog(null)} />}
    </div>
  );
}
